import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { RootState } from '.';
import { authService } from '../service/api/auth.service';
import router from '../router';
import { cookieService } from '../service/cookie.service';
import { StringUtils } from '../utils/string.utils';
import { ApiError } from '../models/core/api-error';
import moment from 'moment';

interface State {
  email: string;
  password: string;
  authCode: string;
  authCodeRequire: boolean;
  authCodeFail: boolean;
  apiError: ApiError;
  isLicenseExpired: boolean;
}

const stateOfModule: State = {
  email: '',
  password: '',
  authCode: '',
  authCodeRequire: false,
  authCodeFail: false,
  apiError: undefined,
  isLicenseExpired: false,
};

const gettersOfModule: GetterTree<State, RootState> = {};

const mutations: MutationTree<State> = {
  email: (state: State, email: string) => {
    state.email = email;
  },
  password: (state: State, password: string) => {
    state.password = password;
  },
  authCode: (state: State, authCode: string) => {
    state.authCode = authCode;
  },
  authCodeRequire: (state: State, authCodeRequire: boolean) => {
    state.authCodeRequire = authCodeRequire;
  },
  authCodeFail: (state: State, authCodeFail: boolean) => {
    state.authCodeFail = authCodeFail;
  },
  apiError: (state: State, value: ApiError) => {
    state.apiError = value;
  },
  isLicenseExpired: (state: State, value: boolean) => {
    state.isLicenseExpired = value;
  },
};

const actions: ActionTree<State, RootState> = {
  async signin(ctx: ActionContext<State, RootState>) {
    ctx.commit('apiError', undefined);
    ctx.commit('isLicenseExpired', false);
    try {
      const auth = await authService.signin(ctx.state.email, ctx.state.password, ctx.state.authCode);
      if (auth) {
        if (auth.licenseExpired) {
          ctx.commit('isLicenseExpired', true);
          return;
        }
        // 2要素認証に関するもの
        // authCodeRequire => メールアドレス & パスワードは通ったので、認証コードを求めている
        // authCodeFail    => 認証コードの入力間違い
        if (auth.authCodeRequire || auth.authCodeFail) {
          // true に更新で、認証コード入力欄の表示
          ctx.commit('authCodeRequire', auth.authCodeRequire);
          // true に更新で、認証コード入力間違いのアラート表示
          ctx.commit('authCodeFail', auth.authCodeFail);
          return;
        }
        // ログイン成功時の処理
        let now: Date = new Date();
        const currentSchoolYear: number = [1, 2, 3].includes(moment(now).month() + 1) ? moment(now).year() - 1 : moment(now).year();
        ctx.dispatch('setSchoolYear', currentSchoolYear, { root: true });
        ctx.dispatch('setAuthToken', auth.authToken, { root: true });
        ctx.dispatch('setAuthInfo', auth, { root: true });
        cookieService.language = StringUtils.browserLanguage();
        ctx.dispatch('fetchAccount', null, { root: true });
        // Get remain ticket count
        ctx.commit('email', '');
        ctx.commit('password', '');
        ctx.commit('authCode', '');
        ctx.commit('authCodeRequire', false);
        ctx.commit('authCodeFail', false);
        router.replace({ path: '/pages/home' });
      }
    } catch (err) {
      ctx.commit('apiError', err);
    }
  },

  async signinOAuth(ctx: ActionContext<State, RootState>, data: any) {
    ctx.commit('apiError', undefined);
    ctx.commit('isLicenseExpired', false);
    try {
      const authInfo = await authService.socialSignin(data.code, data.provider);
      let now: Date = new Date();
      const currentSchoolYear: number = [1, 2, 3].includes(moment(now).month() + 1) ? moment(now).year() - 1 : moment(now).year();
      ctx.dispatch('setSchoolYear', currentSchoolYear, { root: true });
      ctx.dispatch('setAuthToken', authInfo.authToken, { root: true });
      ctx.dispatch('setAuthInfo', authInfo, { root: true });
      cookieService.language = StringUtils.browserLanguage();
      ctx.dispatch('fetchAccount', null, { root: true });
      router.replace({ path: '/pages/home' });
    } catch (err) {
      ctx.commit('apiError', err);
      router.replace({ path: '/signin' });
    }
  },
};

export const SigninModule: Module<State, RootState> = {
  namespaced: true,
  state: stateOfModule,
  getters: gettersOfModule,
  mutations,
  actions,
};
